import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPageContent, getSettings, subscribeToPage, subscribeToSettings } from '../firebase/firebase-config';
import '../styles/rich-text.css'; // Import the rich text CSS

function About() {
  const [pageContent, setPageContent] = useState({
    title: 'About Us',
    history: '',
    mission: '',
    vision: '',
    philosophy: '',
    curriculum: '',
    academicPrograms: '',
    principalName: '',
    principalPhoto: '',
    principalMessage: '',
    directorName: '',
    directorPhoto: '',
    directorMessage: '',
    headmistressName: '',
    headmistressPhoto: '',
    headmistressMessage: '',
    vicePrincipalName: '',
    vicePrincipalPhoto: '',
    vicePrincipalMessage: '',
    deanName: '',
    deanPhoto: '',
    deanMessage: ''
  });
  const [settings, setSettings] = useState({
    primaryColor: '#1a3a6b',
    secondaryColor: '#f59e0b',
    schoolName: 'MIS International School'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [aboutData, settingsData] = await Promise.all([
          getPageContent('about'),
          getSettings()
        ]);
        setPageContent(prev => ({ ...prev, ...aboutData }));
        setSettings(settingsData);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();

    const unsubscribeAbout = subscribeToPage('about', (data) => {
      if (data) setPageContent(prev => ({ ...prev, ...data }));
    });
    
    const unsubscribeSettings = subscribeToSettings((data) => {
      if (data) setSettings(data);
    });

    return () => {
      unsubscribeAbout();
      unsubscribeSettings();
    };
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const primaryColor = settings.primaryColor || '#1a3a6b';
  const secondaryColor = settings.secondaryColor || '#f59e0b';
  const pc = pageContent;

  // Collect all leadership members
  const leadershipMembers = [];
  if (pc.directorName) {
    leadershipMembers.push({
      name: pc.directorName,
      photo: pc.directorPhoto,
      message: pc.directorMessage,
      title: 'Director',
      badgeColor: primaryColor
    });
  }
  if (pc.principalName) {
    leadershipMembers.push({
      name: pc.principalName,
      photo: pc.principalPhoto,
      message: pc.principalMessage,
      title: 'Principal',
      badgeColor: '#7c3aed'
    });
  }
  if (pc.headmistressName) {
    leadershipMembers.push({
      name: pc.headmistressName,
      photo: pc.headmistressPhoto,
      message: pc.headmistressMessage,
      title: 'Headmistress',
      badgeColor: '#0891b2'
    });
  }
  if (pc.vicePrincipalName) {
    leadershipMembers.push({
      name: pc.vicePrincipalName,
      photo: pc.vicePrincipalPhoto,
      message: pc.vicePrincipalMessage,
      title: 'Vice Principal',
      badgeColor: '#d97706'
    });
  }
  if (pc.deanName) {
    leadershipMembers.push({
      name: pc.deanName,
      photo: pc.deanPhoto,
      message: pc.deanMessage,
      title: 'Dean',
      badgeColor: '#475569'
    });
  }

  return (
    <div className="about-page">
      {/* Page Hero */}
        <div className="container">
          <h1>{pc.title || 'About Us'}</h1>
          <div className="hero-divider"></div>
      </div>

      {/* History Section - Using dangerouslySetInnerHTML */}
      {pc.history && (
        <section className="history-section">
          <div className="container">
            <div className="history-content">
              <h2 style={{ color: primaryColor }}>Our History</h2>
              <div className="section-divider" style={{ background: secondaryColor }}></div>
              <div 
                className="history-text rich-text-content"
                dangerouslySetInnerHTML={{ __html: pc.history }}
              />
            </div>
          </div>
        </section>
      )}

      {/* Mission, Vision, Philosophy Section - Using dangerouslySetInnerHTML */}
      {(pc.mission || pc.vision || pc.philosophy) && (
        <section className="mvp-section" style={{ background: '#f8fafc' }}>
          <div className="container">
            <div className="mvp-grid">
              {pc.mission && (
                <div className="mvp-card" style={{ borderTopColor: primaryColor }}>
                  <h3 style={{ color: primaryColor }}>Our Mission</h3>
                  <div className="mvp-divider" style={{ background: secondaryColor }}></div>
                  <div 
                    className="mvp-content rich-text-content"
                    dangerouslySetInnerHTML={{ __html: pc.mission }}
                  />
                </div>
              )}
              {pc.vision && (
                <div className="mvp-card" style={{ borderTopColor: secondaryColor }}>
                  <h3 style={{ color: primaryColor }}>Our Vision</h3>
                  <div className="mvp-divider" style={{ background: secondaryColor }}></div>
                  <div 
                    className="mvp-content rich-text-content"
                    dangerouslySetInnerHTML={{ __html: pc.vision }}
                  />
                </div>
              )}
              {pc.philosophy && (
                <div className="mvp-card" style={{ borderTopColor: primaryColor }}>
                  <h3 style={{ color: primaryColor }}>Our Philosophy</h3>
                  <div className="mvp-divider" style={{ background: secondaryColor }}></div>
                  <div 
                    className="mvp-content rich-text-content"
                    dangerouslySetInnerHTML={{ __html: pc.philosophy }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Academic Information - Using dangerouslySetInnerHTML */}
      {(pc.curriculum || pc.academicPrograms) && (
        <section className="academic-section">
          <div className="container">
            <h2 className="section-title" style={{ color: primaryColor }}>Academic Excellence</h2>
            <div className="section-divider" style={{ background: secondaryColor }}></div>
            <div className="academic-grid">
              {pc.curriculum && (
                <div className="academic-card">
                  <h3 style={{ color: primaryColor }}>📖 Curriculum</h3>
                  <div 
                    className="rich-text-content"
                    dangerouslySetInnerHTML={{ __html: pc.curriculum }}
                  />
                </div>
              )}
              {pc.academicPrograms && (
                <div className="academic-card">
                  <h3 style={{ color: primaryColor }}>🎓 Programs</h3>
                  <div 
                    className="rich-text-content"
                    dangerouslySetInnerHTML={{ __html: pc.academicPrograms }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Leadership Team Section - Using dangerouslySetInnerHTML */}
      {leadershipMembers.length > 0 && (
        <section className="leadership-section" style={{ background: '#f0f4f8' }}>
          <div className="container">
            <p className="eyebrow" style={{ color: secondaryColor }}>About</p>
            <h2 className="section-title" style={{ color: primaryColor }}>Leadership Team</h2>
            <div className="section-divider" style={{ background: secondaryColor }}></div>
            <div className="leader-grid">
              {leadershipMembers.map((member, idx) => (
                <div key={idx} className="leader-card">
                  <div className="leader-image-wrapper">
                    {member.photo ? (
                      <img src={member.photo} alt={member.name} className="leader-image" />
                    ) : (
                      <div className="leader-image-placeholder" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}>
                        {member.title === 'Director' ? '👩‍💼' : member.title === 'Principal' ? '👨‍🏫' : member.title === 'Headmistress' ? '👩‍🏫' : '👨‍🎓'}
                      </div>
                    )}
                    <div className="leader-badge" style={{ background: member.badgeColor }}>
                      <strong>{member.name}</strong>
                      <span>{member.title}</span>
                    </div>
                  </div>
                  <div className="leader-content">
                    <p className="desk-label">
                      FROM THE <span style={{ color: secondaryColor }}>{member.title.toUpperCase()}'S</span> DESK
                    </p>
                    {member.message && (
                      <div 
                        className="leader-message rich-text-content"
                        dangerouslySetInnerHTML={{ __html: member.message }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <style>{`
        .about-page {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          overflow-x: hidden;
        }
        
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
        }
        
        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e2e8f0;
          border-top-color: ${primaryColor};
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 60px 24px;
        }
        
        /* Page Hero */
        .page-hero {
          color: white;
          text-align: center;
          padding: 80px 20px;
        }
        
        .page-hero h1 {
          font-size: 48px;
          margin-bottom: 20px;
        }
        
        .hero-divider {
          width: 60px;
          height: 4px;
          background: white;
          margin: 0 auto 20px;
          border-radius: 2px;
        }
        
        .page-hero p {
          font-size: 18px;
          opacity: 0.9;
        }
        
        /* Section Styles */
        .section-title {
          text-align: center;
          font-size: 36px;
          font-weight: 700;
          margin: 0 0 12px;
        }
        
        .section-divider {
          width: 60px;
          height: 4px;
          margin: 0 auto 40px;
          border-radius: 2px;
        }
        
        .eyebrow {
          text-align: center;
          font-size: 14px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 2px;
          margin-bottom: 8px;
        }
        
        /* History Section */
        .history-section {
          background: white;
        }
        
        .history-content {
          max-width: 800px;
          margin: 0 auto;
          text-align: center;
        }
        
        .history-content h2 {
          font-size: 32px;
          margin-bottom: 20px;
        }
        
        /* MVP Section */
        .mvp-section {
          padding: 60px 0;
        }
        
        .mvp-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 40px;
        }
        
        .mvp-card {
          background: white;
          border-radius: 20px;
          padding: 35px;
          border-top: 4px solid;
          box-shadow: 0 4px 20px rgba(0,0,0,0.05);
          transition: transform 0.3s;
        }
        
        .mvp-card:hover {
          transform: translateY(-5px);
        }
        
        .mvp-card h3 {
          font-size: 28px;
          margin-bottom: 20px;
          text-align: center;
        }
        
        .mvp-divider {
          width: 50px;
          height: 3px;
          margin: 0 auto 25px;
          border-radius: 2px;
        }
        
        /* Academic Section */
        .academic-section {
          background: #f8fafc;
          padding: 60px 0;
        }
        
        .academic-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 30px;
        }
        
        .academic-card {
          background: white;
          padding: 30px;
          border-radius: 20px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.08);
          transition: transform 0.3s;
        }
        
        .academic-card:hover {
          transform: translateY(-3px);
        }
        
        .academic-card h3 {
          margin-bottom: 15px;
          font-size: 22px;
        }
        
        /* Leadership Section */
        .leadership-section {
          padding: 60px 0;
        }
        
        .leader-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
          gap: 32px;
          align-items: stretch;
        }
        
        .leader-card {
          background: #fff;
          border-radius: 28px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        
        .leader-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 55px rgba(0,0,0,0.16);
        }
        
        .leader-image-wrapper {
          position: relative;
          width: 100%;
          height: 340px;
          background: #000;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        
        .leader-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
          background: #000;
        }
        
        .leader-image-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 80px;
          color: white;
        }
        
        .leader-badge {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          padding: 18px 16px;
          text-align: center;
          backdrop-filter: blur(4px);
        }
        
        .leader-badge strong {
          color: #fff;
          font-size: 24px;
          display: block;
          margin-bottom: 6px;
          font-weight: 700;
          text-transform: capitalize;
        }
        
        .leader-badge span {
          color: rgba(255,255,255,0.95);
          font-size: 14px;
          letter-spacing: 2px;
          text-transform: uppercase;
        }
        
        .leader-content {
          padding: 30px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }
        
        .desk-label {
          font-size: 15px;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: 1px;
          text-transform: uppercase;
          margin-bottom: 18px;
          line-height: 1.6;
        }
        
        .leader-message {
          flex: 1;
        }
        
        /* Responsive */
        @media (max-width: 1024px) {
          .container {
            padding: 50px 20px;
          }
          
          .leader-grid {
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          }
          
          .leader-image-wrapper {
            height: 300px;
          }
        }
        
        @media (max-width: 768px) {
          .container {
            padding: 40px 16px;
          }
          
          .page-hero {
            padding: 60px 20px;
          }
          
          .page-hero h1 {
            font-size: 32px;
          }
          
          .section-title {
            font-size: 28px;
          }
          
          .mvp-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          
          .mvp-card {
            padding: 24px;
          }
          
          .mvp-card h3 {
            font-size: 24px;
          }
          
          .academic-grid {
            grid-template-columns: 1fr;
          }
          
          .leader-grid {
            grid-template-columns: 1fr;
            gap: 26px;
          }
          
          .leader-image-wrapper {
            height: 320px;
          }
          
          .leader-badge {
            padding: 16px 12px;
          }
          
          .leader-badge strong {
            font-size: 20px;
          }
          
          .leader-content {
            padding: 24px 22px;
            text-align: center;
          }
          
          .desk-label {
            font-size: 14px;
            text-align: center;
          }
        }
        
        @media (max-width: 480px) {
          .page-hero {
            padding: 50px 16px;
          }
          
          .page-hero h1 {
            font-size: 28px;
          }
          
          .section-title {
            font-size: 24px;
          }
          
          .history-content h2 {
            font-size: 24px;
          }
          
          .leader-image-wrapper {
            height: 360px;
          }
          
          .leader-content {
            padding: 22px 18px;
          }
          
          .leader-badge strong {
            font-size: 18px;
          }
        }
      `}</style>
    </div>
  );
}

export default About;